import { useEffect, useMemo, useRef, useState } from "react";
import { AtSign, Send } from "lucide-react";
import { Avatar } from "../bits";

const firstName = (n = "") => n.split(" ")[0];
// Handle a person types to mention them: first name, or full name without spaces when two people share a first name.
export const handleFor = (p, all) => (all.filter((x) => firstName(x.name).toLowerCase() === firstName(p.name).toLowerCase()).length > 1 ? p.name.replace(/\s+/g, "") : firstName(p.name));

// COMPOSER — Enter sends, Shift+Enter breaks a line, "@" opens a people picker (plus @team on job/project threads).
export function Composer({ participants = [], me, allowTeam, onSend, disabled, placeholder = "Write a message…" }) {
  const [text, setText] = useState("");
  const [mentions, setMentions] = useState([]);
  const [pick, setPick] = useState(null); // { q, start }
  const [idx, setIdx] = useState(0);
  const ref = useRef(null);
  const others = useMemo(() => participants.filter((p) => p.id !== me), [participants, me]);
  const options = useMemo(() => {
    if (!pick) return [];
    const q = pick.q.toLowerCase();
    const ppl = others.filter((p) => p.name.toLowerCase().includes(q) || handleFor(p, others).toLowerCase().startsWith(q)).slice(0, 6).map((p) => ({ id: p.id, handle: handleFor(p, others), name: p.name, sub: p.trade || p.role?.replace("_", " ") }));
    return allowTeam && "team".startsWith(q) ? [{ id: "team", handle: "team", name: "Everyone on this thread", sub: "notifies all participants" }, ...ppl] : ppl;
  }, [pick, others, allowTeam]);
  useEffect(() => { setIdx(0); }, [pick?.q]);
  useEffect(() => { const el = ref.current; if (el) { el.style.height = "0px"; el.style.height = Math.min(160, el.scrollHeight) + "px"; } }, [text]);

  const onChange = (e) => {
    const v = e.target.value; setText(v);
    const caret = e.target.selectionStart;
    const m = /(^|\s)@([\w'.-]*)$/.exec(v.slice(0, caret));
    setPick(m ? { q: m[2], start: caret - m[2].length - 1 } : null);
  };
  const choose = (o) => {
    const caret = ref.current.selectionStart;
    const next = `${text.slice(0, pick.start)}@${o.handle} ${text.slice(caret)}`;
    setText(next); setPick(null);
    if (o.id !== "team") setMentions((m) => (m.includes(o.id) ? m : [...m, o.id]));
    requestAnimationFrame(() => { const pos = pick.start + o.handle.length + 2; ref.current?.setSelectionRange(pos, pos); ref.current?.focus(); });
  };
  const send = () => {
    const t = text.trim();
    if (!t || disabled) return;
    onSend(t, mentions.filter((id) => t.includes(`@${handleFor(others.find((p) => p.id === id) || { name: "" }, others)}`)));
    setText(""); setMentions([]); setPick(null);
  };
  const onKey = (e) => {
    if (pick && options.length) {
      if (e.key === "ArrowDown") { e.preventDefault(); setIdx((i) => (i + 1) % options.length); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setIdx((i) => (i - 1 + options.length) % options.length); return; }
      if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); choose(options[idx]); return; }
      if (e.key === "Escape") { setPick(null); return; }
    }
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };
  return (
    <div className="relative border-t border-line bg-surface p-2.5" data-testid="composer">
      {pick && options.length > 0 && (
        <div className="card absolute bottom-full left-3 z-20 mb-1 w-72 overflow-hidden shadow-xl animate-in" data-testid="mention-picker">
          <div className="t4 flex items-center gap-1 border-b border-line px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wide"><AtSign size={10} /> Mention</div>
          {options.map((o, i) => <button key={o.id} type="button" onMouseDown={(e) => { e.preventDefault(); choose(o); }} className={`flex w-full items-center gap-2 px-2.5 py-1.5 text-left ${i === idx ? "bg-amber-500/10" : "hover:bg-surface-2"}`} data-testid={`mention-${o.id}`}>{o.id === "team" ? <span className="flex h-6 w-6 items-center justify-center rounded-full bg-zinc-900 text-[10px] font-bold text-amber-400">@</span> : <Avatar name={o.name} size="sm" />}<span className="min-w-0 flex-1"><span className="t1 block truncate text-[12.5px] font-medium">@{o.handle}</span><span className="t3 block truncate text-[10.5px]">{o.id === "team" ? o.name : `${o.name}${o.sub ? ` · ${o.sub}` : ""}`}</span></span></button>)}
        </div>
      )}
      <div className="flex items-end gap-2">
        <textarea ref={ref} rows={1} className="input min-h-[38px] flex-1 resize-none !py-2 text-[13.5px] leading-snug" placeholder={placeholder} value={text} onChange={onChange} onKeyDown={onKey} disabled={disabled} data-testid="composer-input" />
        <button type="button" className="btn-primary !h-[38px] !px-3" onClick={send} disabled={disabled || !text.trim()} data-testid="composer-send" title="Send (Enter)"><Send size={15} /></button>
      </div>
      <div className="t4 mt-1 px-0.5 text-[10px]">Enter to send · Shift+Enter for a new line · @ to mention{allowTeam ? " · @team for everyone" : ""}</div>
    </div>
  );
}
